import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tag,
  Scale,
  Plus,
  Printer,
  Trash2,
  Camera,
  Layers,
  RefreshCw,
  ChevronDown,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PesoIcon } from '@/components/common/PesoIcon';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { PigSaleModal } from '@/components/sales/PigSaleModal';
import { ScaleSlipPrintModal } from '@/components/sales/ScaleSlipPrintModal';
import { ScaleItemsDetailModal } from '@/components/sales/ScaleItemsDetailModal';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { useAuth } from '@/context/AuthContext';
import { salesService } from '@/services/salesService';
import { cycleService } from '@/services/cycleService';
import { formatDate, formatCurrency } from '@/lib/utils';
import {
  PigSaleRecord,
  BuyerRecord,
  ProductionCycleRecord,
  DocumentRecord,
} from '@/types/database';

export const PigSalesPage: React.FC = () => {
  const { isOperations, user } = useAuth();

  const [sales, setSales] = useState<PigSaleRecord[]>([]);
  const [buyers, setBuyers] = useState<BuyerRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Year filter state (defaults to latest year with data)
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Filters
  const [search, setSearch] = useState<string>('');
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>('');
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');

  // Expandable row drawer state
  const [expandedSaleId, setExpandedSaleId] = useState<string | null>(null);

  // Modals
  const [saleModalOpen, setSaleModalOpen] = useState<boolean>(false);
  const [selectedSaleForPrint, setSelectedSaleForPrint] = useState<PigSaleRecord | null>(null);
  const [selectedSaleForItems, setSelectedSaleForItems] = useState<PigSaleRecord | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentRecord | null>(null);
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    saleId: string;
    saleIdentifier: string;
  }>({
    isOpen: false,
    saleId: '',
    saleIdentifier: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [salesData, buyersData, cyclesData] = await Promise.all([
        salesService.getPigSales({
          buyerId: selectedBuyerId || undefined,
          cycleId: selectedCycleId || undefined,
        }),
        salesService.getBuyers(),
        cycleService.getCycles(),
      ]);

      setSales(salesData);
      setBuyers(buyersData);
      setCycles(cyclesData);
    } catch (err: any) {
      console.error('[PigSalesPage] Error loading data:', err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBuyerId, selectedCycleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derive available years dynamically from live sale records
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    sales.forEach((s) => {
      if (s.sale_date) {
        const yr = s.sale_date.substring(0, 4);
        if (/^\d{4}$/.test(yr)) yearsSet.add(yr);
      }
    });

    if (yearsSet.size === 0) {
      yearsSet.add('2026');
      yearsSet.add('2025');
      yearsSet.add('2024');
    }

    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [sales]);

  // Synchronize default year if current selected is not in list
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Year-filtered Sales
  const yearFilteredSales = useMemo(() => {
    if (selectedYear === 'all') return sales;
    return sales.filter((s) => s.sale_date?.startsWith(selectedYear));
  }, [sales, selectedYear]);

  // Search-filtered Sales
  const filteredSales = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return yearFilteredSales;

    return yearFilteredSales.filter((s) => {
      const ticket = s.scale_ticket_no?.toLowerCase() || '';
      const gatePass = s.gate_pass_no?.toLowerCase() || '';
      const saleNum = s.sale_number?.toLowerCase() || '';
      const buyer = s.buyers?.buyer_name?.toLowerCase() || '';
      const cycle = s.production_cycles?.cycle_code?.toLowerCase() || '';
      const grower = s.production_cycles?.growers?.full_name?.toLowerCase() || '';
      const raiserLegacy = s.legacy_raiser_name?.toLowerCase() || '';
      return (
        ticket.includes(term) ||
        gatePass.includes(term) ||
        saleNum.includes(term) ||
        buyer.includes(term) ||
        cycle.includes(term) ||
        grower.includes(term) ||
        raiserLegacy.includes(term)
      );
    });
  }, [yearFilteredSales, search]);

  const handleClearFilters = () => {
    setSearch('');
    setSelectedBuyerId('');
    setSelectedCycleId('');
  };

  const handleSaleCreated = (newSale: PigSaleRecord) => {
    setSaleModalOpen(false);
    loadData();
    setSelectedSaleForPrint(newSale);
  };

  const handleSoftVoidConfirm = async (reason: string) => {
    if (!voidModalState.saleId) return;
    try {
      await salesService.softVoidSale(voidModalState.saleId, reason, user?.id);
      setVoidModalState({ isOpen: false, saleId: '', saleIdentifier: '' });
      loadData();
    } catch (err: any) {
      alert(`Error voiding sale: ${err.message}`);
    }
  };

  // Aggregated KPIs across year-filtered sales
  const totalLifts = yearFilteredSales.length;
  const totalSoldHead = yearFilteredSales.reduce((sum, s) => sum + (Number(s.head_count) || 0), 0);
  const totalGrossKg = yearFilteredSales.reduce((sum, s) => sum + (Number(s.gross_weight_kg) || 0), 0);
  const totalShrinkKg = yearFilteredSales.reduce((sum, s) => sum + (Number(s.shrinkage_deduction_kg) || 0), 0);
  const totalNetKg = yearFilteredSales.reduce((sum, s) => sum + (Number(s.net_weight_kg) || 0), 0);
  const totalRevenue = yearFilteredSales.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
  const avgNetPerHead = totalSoldHead > 0 ? totalNetKg / totalSoldHead : 0;
  const avgPricePerKg = totalNetKg > 0 ? totalRevenue / totalNetKg : 0;

  // Buyer Combobox Options
  const buyerOptions = useMemo(() => {
    return buyers.map((b) => ({
      value: b.id,
      label: b.buyer_name,
    }));
  }, [buyers]);

  // Cycle Combobox Options
  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
      badge: c.status,
    }));
  }, [cycles]);

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Standardized Page Header */}
      <PageHeader
        title={selectedYear === 'all' ? 'Pig Sales & Scale — All Years' : `Pig Sales & Scale — ${selectedYear}`}
        subtitle="Buyer orders, scale chute weighing tickets, dynamic shrinkage, and revenue liquidation."
        icon={Scale}
        helpScreenKey="sales"
        actions={
          <>
            <ActionTooltip content="Reload commercial pig sales, scale slips, and buyer balances for this year.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                className="text-xs h-8 sm:h-9 text-slate-700 hover:bg-slate-50 gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {isOperations && (
              <ActionTooltip content="Record live scale chute weighings, head count, tare shrinkage %, and buyer agreed price.">
                <Button
                  size="sm"
                  onClick={() => setSaleModalOpen(true)}
                  className="text-xs h-8 sm:h-9 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold gap-1.5 shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5 text-emerald-200" />
                  <span>New Scale Weighing Ticket</span>
                </Button>
              </ActionTooltip>
            )}
          </>
        }
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        }
      />

      {/* 2. Standardized KPI Summary Cards (4 Cards) */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Total Sale Lifts"
          value={`${totalLifts.toLocaleString()} lifts`}
          subtitle={selectedYear === 'all' ? 'All historical weighings' : `${totalLifts} in ${selectedYear}`}
          icon={Layers}
          variant="default"
        />
        <KpiCard
          title="Total Pigs Sold"
          value={`${totalSoldHead.toLocaleString()} head`}
          subtitle={`${avgNetPerHead.toFixed(1)} kg/head avg net weight`}
          icon={Tag}
          variant="emerald"
        />
        <KpiCard
          title="Net Weight Sold"
          value={`${totalNetKg.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`}
          subtitle={`Gross: ${totalGrossKg.toLocaleString()} kg • Shrink: -${totalShrinkKg.toLocaleString()} kg`}
          icon={Scale}
          variant="default"
        />
        <KpiCard
          title="Total Sales Revenue"
          value={formatCurrency(totalRevenue)}
          subtitle={`Avg Rate: ₱${avgPricePerKg.toFixed(2)}/kg net`}
          icon={PesoIcon}
          variant="emerald"
        />
      </KpiCardGroup>

      {/* 3. Standardized FilterBar */}
      <FilterBar
        searchTerm={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search ticket, buyer, cycle, gate pass..."
        onReset={handleClearFilters}
        showReset={Boolean(search || selectedBuyerId || selectedCycleId)}
      >
        {/* Buyer Combobox */}
        <div className="w-full sm:w-60">
          <Combobox
            options={buyerOptions}
            value={selectedBuyerId}
            onChange={setSelectedBuyerId}
            allowAll={true}
            allLabel="All Buyers / Customers"
            placeholder="Filter by Buyer..."
            searchPlaceholder="Search buyer..."
          />
        </div>

        {/* Production Cycle Combobox */}
        <div className="w-full sm:w-64">
          <Combobox
            options={cycleOptions}
            value={selectedCycleId}
            onChange={setSelectedCycleId}
            allowAll={true}
            allLabel="All Production Cycles"
            placeholder="Filter by Cycle..."
            searchPlaceholder="Search cycle code or grower..."
          />
        </div>
      </FilterBar>

      {/* 4. Sales Records Content */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs font-medium">
            Loading pig sales records from live database...
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs">
            No pig sales found matching the filter criteria.
          </div>
        ) : (
          <>
            {/* Desktop 8-Column Progressive Disclosure Table (md:table) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse font-sans">
                <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-2.5 px-3 whitespace-nowrap w-24">Date</th>
                    <th className="py-2.5 px-3 whitespace-nowrap">Ticket / Ref #</th>
                    <th className="py-2.5 px-3">Buyer / Customer</th>
                    <th className="py-2.5 px-3">Cycle & Raiser</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap">Head</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap">Net Weight (kg)</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap">Total Amount (₱)</th>
                    <th className="py-2.5 px-2 text-center w-12">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredSales.map((sale) => {
                    const isExpanded = expandedSaleId === sale.id;
                    const itemsCount = sale.pig_sale_items?.length || 0;
                    const docCount = sale.documents?.length || 0;

                    return (
                      <React.Fragment key={sale.id}>
                        <tr
                          onClick={() => setExpandedSaleId(isExpanded ? null : sale.id)}
                          className={`cursor-pointer transition-colors ${
                            isExpanded ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap">
                            {formatDate(sale.sale_date)}
                          </td>
                          <td className="py-2.5 px-3 font-mono font-semibold text-slate-900 whitespace-nowrap">
                            {sale.scale_ticket_no ? (
                              <span>ST #{sale.scale_ticket_no}</span>
                            ) : (
                              <span className="text-slate-500">{sale.sale_number || '—'}</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-medium text-slate-800 truncate max-w-[160px]">
                            {sale.buyers?.buyer_name || 'Direct Buyer'}
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="font-mono font-semibold text-slate-900 block">
                              {sale.production_cycles?.cycle_code || '—'}
                            </span>
                            <span className="text-[11px] text-slate-500 block truncate max-w-[180px]">
                              {sale.production_cycles?.growers?.full_name || sale.legacy_raiser_name || 'VSB'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                            {Number(sale.head_count).toLocaleString()} hd
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-800 whitespace-nowrap">
                            {Number(sale.net_weight_kg).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                            {formatCurrency(Number(sale.total_amount))}
                          </td>
                          <td className="py-2.5 px-2 text-center text-slate-400">
                            <ChevronDown
                              className={`w-4 h-4 mx-auto transition-transform duration-150 ${
                                isExpanded ? 'transform rotate-180 text-emerald-700' : ''
                              }`}
                            />
                          </td>
                        </tr>

                        {/* Progressive Disclosure Row Drawer */}
                        {isExpanded && (
                          <tr className="bg-slate-50/90 border-b border-slate-200">
                            <td colSpan={8} className="p-3.5 sm:p-4">
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs bg-white p-3 rounded-lg border border-slate-200 shadow-inner">
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Base Price</span>
                                  <span className="font-mono font-semibold text-slate-800">
                                    ₱{Number(sale.price_per_kg).toFixed(2)} / kg
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Gross Weight</span>
                                  <span className="font-mono text-slate-800">
                                    {Number(sale.gross_weight_kg).toLocaleString()} kg
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Shrinkage Deduction</span>
                                  <span className="font-mono text-amber-800">
                                    -{Number(sale.shrinkage_deduction_kg).toLocaleString()} kg ({sale.shrinkage_rate_applied || 0} kg/hd)
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Gate Pass / Vehicle</span>
                                  <span className="font-mono text-slate-700">
                                    {sale.gate_pass_no ? `GP #${sale.gate_pass_no}` : '—'} {sale.vehicle_plate_no ? `(${sale.vehicle_plate_no})` : ''}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Average Weight</span>
                                  <span className="font-mono text-slate-800">
                                    {Number(sale.head_count) > 0
                                      ? `${(Number(sale.net_weight_kg) / Number(sale.head_count)).toFixed(1)} kg / hd`
                                      : '—'}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-200 text-xs">
                                <div className="flex items-center gap-2">
                                  {/* View Items Breakdown */}
                                  <ActionTooltip content="Inspect individual scale chute weighing lifts and head counts.">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSaleForItems(sale);
                                      }}
                                      className="text-xs h-7 gap-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                                    >
                                      <Eye className="w-3.5 h-3.5 text-slate-500" />
                                      <span>Scale Items ({itemsCount > 0 ? itemsCount : sale.head_count})</span>
                                    </Button>
                                  </ActionTooltip>

                                  {/* Print Scale Slip */}
                                  <ActionTooltip content="Generate formatted physical weighment slip for buyer gate clearance.">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSaleForPrint(sale);
                                      }}
                                      className="text-xs h-7 gap-1 border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                                    >
                                      <Printer className="w-3.5 h-3.5 text-emerald-700" />
                                      <span>Print Scale Slip</span>
                                    </Button>
                                  </ActionTooltip>

                                  {/* Document Preview */}
                                  {docCount > 0 ? (
                                    <ActionTooltip content="Open scanned physical scale ticket photo.">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (sale.documents && sale.documents.length > 0) {
                                            setPreviewDoc(sale.documents[0]);
                                          }
                                        }}
                                        className="text-xs h-7 gap-1 border-indigo-300 text-indigo-800 bg-indigo-50 hover:bg-indigo-100"
                                      >
                                        <Camera className="w-3.5 h-3.5 text-indigo-700" /> View Scale Slip Photo
                                      </Button>
                                    </ActionTooltip>
                                  ) : (
                                    <span className="text-slate-400 italic text-[11px] ml-1">No ticket scan on file</span>
                                  )}
                                  {sale.notes && <span className="text-slate-500 italic ml-2">Notes: {sale.notes}</span>}
                                </div>

                                {isOperations && (
                                  <ActionTooltip content="Void this commercial sales record with an auditable justification.">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setVoidModalState({
                                          isOpen: true,
                                          saleId: sale.id,
                                          saleIdentifier: `${sale.scale_ticket_no ? `Ticket #${sale.scale_ticket_no}` : sale.sale_number || 'Sale'} (${sale.head_count} head)`,
                                        });
                                      }}
                                      className="text-xs h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" /> Soft-Void Sale
                                    </Button>
                                  </ActionTooltip>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Responsive Cards Layout (md:hidden) */}
            <div className="md:hidden divide-y divide-slate-200">
              {filteredSales.map((sale) => {
                const isExpanded = expandedSaleId === sale.id;
                const docCount = sale.documents?.length || 0;
                const itemsCount = sale.pig_sale_items?.length || 0;

                return (
                  <div
                    key={sale.id}
                    onClick={() => setExpandedSaleId(isExpanded ? null : sale.id)}
                    className="p-3.5 space-y-2 hover:bg-slate-50 cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-mono text-slate-500">{formatDate(sale.sale_date)}</span>
                      <span className="text-sm font-bold font-mono text-slate-900">
                        {formatCurrency(Number(sale.total_amount))}
                      </span>
                    </div>

                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold text-slate-900">
                          {sale.scale_ticket_no ? `ST #${sale.scale_ticket_no}` : sale.sale_number || 'Sale Lift'}
                        </p>
                        <p className="text-xs text-slate-700 font-medium">{sale.buyers?.buyer_name || 'Direct Buyer'}</p>
                        <p className="text-[11px] text-slate-500">
                          {sale.production_cycles?.cycle_code} • {sale.production_cycles?.growers?.full_name || sale.legacy_raiser_name || 'VSB'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded font-mono font-bold text-xs border border-emerald-200">
                          {Number(sale.head_count)} hd
                        </span>
                        <span className="text-[11px] font-mono font-semibold text-slate-600">
                          {Number(sale.net_weight_kg).toLocaleString()} kg
                        </span>
                      </div>
                    </div>

                    {/* Mobile Expanded Drawer */}
                    {isExpanded && (
                      <div className="pt-2.5 mt-2 border-t border-slate-100 space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2.5 rounded-lg">
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase font-bold block">Base Price</span>
                            <span className="font-mono text-slate-800">₱{Number(sale.price_per_kg).toFixed(2)} / kg</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase font-bold block">Shrinkage</span>
                            <span className="font-mono text-amber-800">-{Number(sale.shrinkage_deduction_kg)} kg</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSaleForItems(sale);
                              }}
                              className="text-xs text-slate-700 font-semibold flex items-center gap-1 border border-slate-300 rounded px-2 py-1 bg-white"
                            >
                              <Eye className="w-3.5 h-3.5 text-slate-500" /> Items ({itemsCount > 0 ? itemsCount : sale.head_count})
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSaleForPrint(sale);
                              }}
                              className="text-xs text-emerald-800 font-semibold flex items-center gap-1 border border-emerald-300 rounded px-2 py-1 bg-emerald-50"
                            >
                              <Printer className="w-3.5 h-3.5" /> Slip
                            </button>
                            {docCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (sale.documents && sale.documents.length > 0) {
                                    setPreviewDoc(sale.documents[0]);
                                  }
                                }}
                                className="text-xs text-indigo-700 font-semibold flex items-center gap-1"
                              >
                                <Camera className="w-3.5 h-3.5" /> Scan
                              </button>
                            )}
                          </div>

                          {isOperations && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVoidModalState({
                                  isOpen: true,
                                  saleId: sale.id,
                                  saleIdentifier: `${sale.scale_ticket_no ? `Ticket #${sale.scale_ticket_no}` : sale.sale_number || 'Sale'} (${sale.head_count} head)`,
                                });
                              }}
                              className="text-xs text-rose-600 font-semibold flex items-center gap-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Void
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pig Sale Modal Dialog */}
      <PigSaleModal
        isOpen={saleModalOpen}
        onClose={() => setSaleModalOpen(false)}
        onSuccess={handleSaleCreated}
      />

      {/* Printable Scale Slip Modal */}
      <ScaleSlipPrintModal
        isOpen={!!selectedSaleForPrint}
        onClose={() => setSelectedSaleForPrint(null)}
        sale={selectedSaleForPrint}
      />

      {/* Scale Items Detail Modal */}
      <ScaleItemsDetailModal
        isOpen={!!selectedSaleForItems}
        onClose={() => setSelectedSaleForItems(null)}
        sale={selectedSaleForItems}
        onPrintSlip={() => {
          setSelectedSaleForPrint(selectedSaleForItems);
          setSelectedSaleForItems(null);
        }}
      />

      {/* Document Photo Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />

      {/* Soft Void Confirmation Modal */}
      <SoftVoidConfirmModal
        isOpen={voidModalState.isOpen}
        onClose={() => setVoidModalState({ isOpen: false, saleId: '', saleIdentifier: '' })}
        onConfirm={handleSoftVoidConfirm}
        title="Confirm Pig Sale Voiding"
        recordIdentifier={voidModalState.saleIdentifier}
      />
    </div>
  );
};
